import React from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import {
  AlertCircle,
  BookOpenCheck,
  CheckCircle2,
  Download,
  Filter,
  Loader2,
  Printer,
  ReceiptText,
  RefreshCw,
  Search,
  X,
  Users,
} from 'lucide-react';
import { summativeExamService } from '../../../services/summativeExamService';
import { systemService } from '../../../services/systemService';
import { useAuth } from '../../../contexts/AuthContext';
import Select from '../../../components/ui/Select';
import SummativeMarksEntry from './SummativeMarksEntry';
import SummativeResultApprovals from './SummativeResultApprovals';
import StaffAcademicProgress from './StaffAcademicProgress';

const money = value => `KES ${Number(value || 0).toLocaleString('en-KE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const classLabel = row => `Level ${row.program_level || '—'} · Module ${row.module_number || '—'}`;

const DepartmentSummativeExamDashboard = () => {
  const { profile } = useAuth();
  const assignedRoles = [profile?.primary_role, ...(profile?.secondary_roles || [])].filter(Boolean);
  const trainerRoles = ['trainer_lecturer', 'trainer', 'teacher', 'lecturer'];
  const leadershipRoles = ['system_administrator', 'chief_principal', 'deputy_principal_academic', 'registrar_academic', 'dean_of_faculty', 'head_of_department', 'head_of_section', 'assessment_officer'];
  const approvalRoles = [...leadershipRoles, 'quality_assurance_officer', 'senate', 'senate_member', 'senate_secretary'];
  // Marks are entered by assigned trainers. Academic leadership only reviews
  // and advances the approval batch through the controlled release workflow.
  const canEnterMarks = assignedRoles.some(role => trainerRoles.includes(role) || ['system_administrator', 'assessment_officer'].includes(role));
  const canViewBookings = assignedRoles.some(role => leadershipRoles.includes(role));
  const canReviewApprovals = assignedRoles.some(role => approvalRoles.includes(role));
  const canViewHistorical = canViewBookings;
  const [activeTab, setActiveTab] = React.useState(canViewBookings ? 'bookings' : (canReviewApprovals ? 'approvals' : 'marks'));
  const [payload, setPayload] = React.useState({ scope: null, bookings: [] });
  const [institution, setInstitution] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [notice, setNotice] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const [programId, setProgramId] = React.useState('all');
  const [moduleKey, setModuleKey] = React.useState('all');
  const [unitId, setUnitId] = React.useState('all');
  const [period, setPeriod] = React.useState('all');
  const [attendanceOpen, setAttendanceOpen] = React.useState(false);
  const [attendanceKey, setAttendanceKey] = React.useState('');
  const [attendanceDetails, setAttendanceDetails] = React.useState({ examDate: '', examTime: '', venue: '', examType: '', chiefInvigilator: '' });
  const [attendanceDownloading, setAttendanceDownloading] = React.useState(false);
  const [attendanceBulkDownloading, setAttendanceBulkDownloading] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!canViewBookings) return;
    setLoading(true);
    setNotice(null);
    try {
      const [bookingsPayload, institutionDetails] = await Promise.all([
        summativeExamService.getDepartmentBookings(),
        systemService.getInstitutionDetails(),
      ]);
      setPayload(bookingsPayload);
      setInstitution(institutionDetails);
    } catch (error) {
      setNotice(error.message);
    } finally {
      setLoading(false);
    }
  }, [canViewBookings]);

  React.useEffect(() => {
    if (!canViewBookings && canEnterMarks && !canReviewApprovals) setActiveTab('marks');
    if (!canViewBookings && canReviewApprovals && !canEnterMarks) setActiveTab('approvals');
    if (canViewBookings) load();
  }, [canViewBookings, canEnterMarks, canReviewApprovals, load]);

  const bookings = payload.bookings || [];
  const programs = React.useMemo(() => {
    const unique = new Map();
    bookings.forEach(row => unique.set(String(row.program_id), { id: String(row.program_id), name: row.program_name || row.program_code || 'Programme' }));
    return [...unique.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [bookings]);
  const moduleKeyFor = row => `${row.program_level ?? ''}:${row.module_number ?? ''}`;
  const moduleLabelFor = row => `Level ${row.program_level || '—'} · Module ${row.module_number || '—'}`;
  const modules = React.useMemo(() => {
    const unique = new Map();
    bookings
      .filter(row => programId === 'all' || String(row.program_id) === programId)
      .forEach(row => unique.set(moduleKeyFor(row), { id: moduleKeyFor(row), name: moduleLabelFor(row) }));
    return [...unique.values()].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }, [bookings, programId]);
  const units = React.useMemo(() => {
    const unique = new Map();
    bookings
      .filter(row => row.unit_id
        && (programId === 'all' || String(row.program_id) === programId)
        && (moduleKey === 'all' || moduleKeyFor(row) === moduleKey))
      .forEach(row => unique.set(String(row.unit_id), {
        id: String(row.unit_id),
        code: row.unit_code || '',
        name: row.unit_name || row.unit_code || 'Unit of competency',
      }));
    return [...unique.values()].sort((a, b) => `${a.code} ${a.name}`.localeCompare(`${b.code} ${b.name}`));
  }, [bookings, programId, moduleKey]);
  React.useEffect(() => {
    if (moduleKey !== 'all' && !modules.some(module => module.id === String(moduleKey))) setModuleKey('all');
  }, [moduleKey, modules]);
  React.useEffect(() => {
    if (unitId !== 'all' && !units.some(unit => unit.id === String(unitId))) setUnitId('all');
  }, [unitId, units]);
  const periods = React.useMemo(() => {
    const unique = new Map();
    bookings.forEach(row => unique.set(`${row.academic_year_id}:${row.semester_id}`, `${row.academic_year_name || 'Year'} · ${row.semester_name || 'Period'}`));
    return [...unique.entries()];
  }, [bookings]);

  const filtered = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    return bookings.filter(row => {
      if (programId !== 'all' && String(row.program_id) !== programId) return false;
      if (moduleKey !== 'all' && moduleKeyFor(row) !== moduleKey) return false;
      if (unitId !== 'all' && String(row.unit_id) !== unitId) return false;
      if (period !== 'all' && `${row.academic_year_id}:${row.semester_id}` !== period) return false;
      if (term && ![row.student_name, row.admission_number, row.unit_code, row.unit_name].some(value => String(value || '').toLowerCase().includes(term))) return false;
      return true;
    });
  }, [bookings, programId, moduleKey, unitId, period, search]);

  const students = React.useMemo(() => {
    const grouped = new Map();
    filtered.forEach(row => {
      const key = `${row.program_id}:${row.program_level}:${row.module_number}:${row.student_id}`;
      const existing = grouped.get(key) || {
        key,
        studentName: row.student_name,
        admissionNumber: row.admission_number,
        programName: row.program_name,
        className: classLabel(row),
        periodName: `${row.academic_year_name || 'Year'} · ${row.semester_name || 'Period'}`,
        units: [],
        total: 0,
      };
      existing.units.push(row);
      existing.total += Number(row.fee_amount || 0);
      grouped.set(key, existing);
    });
    return [...grouped.values()].sort((a, b) => `${a.programName}${a.className}${a.studentName}`.localeCompare(`${b.programName}${b.className}${b.studentName}`));
  }, [filtered]);

  const billedTotal = filtered.reduce((sum, row) => sum + Number(row.fee_amount || 0), 0);
  const allBilled = filtered.filter(row => row.billed).length;
  const attendanceGroups = React.useMemo(() => {
    const grouped = new Map();
    // The exam attendance register is generated only for paid candidates. A
    // candidate also needs a recorded class-attendance percentage of at least
    // 70% to appear in that register.
    filtered.filter(row => row.billed).forEach(row => {
      const key = [row.academic_year_id, row.semester_id, row.program_id, row.program_level, row.module_number, row.unit_id].join(':');
      const existing = grouped.get(key) || {
        key,
        programName: row.program_name || row.program_code || 'Programme',
        programLevel: row.program_level,
        moduleNumber: row.module_number,
        unitId: row.unit_id,
        unitCode: row.unit_code || 'Unit',
        unitName: row.unit_name || '',
        unitType: row.unit_type || '',
        periodName: `${row.academic_year_name || 'Year'} · ${row.semester_name || 'Period'}`,
        students: [],
      };
      existing.students.push(row);
      grouped.set(key, existing);
    });
    return [...grouped.values()]
      .map(group => {
        const allStudents = [...group.students].sort((a, b) => String(a.admission_number || a.student_name || '').localeCompare(String(b.admission_number || b.student_name || '')));
        const eligibleStudents = allStudents.filter(row => row.attendance_recorded && Number(row.attendance_percentage) >= 70);
        return {
          ...group,
          allStudents,
          students: eligibleStudents,
          attendanceMissing: allStudents.filter(row => !row.attendance_recorded).length,
          attendanceBelowThreshold: allStudents.filter(row => row.attendance_recorded && Number(row.attendance_percentage) < 70).length,
        };
      })
      .sort((a, b) => `${a.programName}${a.programLevel}${a.moduleNumber}${a.unitCode}`.localeCompare(`${b.programName}${b.programLevel}${b.moduleNumber}${b.unitCode}`));
  }, [filtered]);
  const activeAttendanceGroup = attendanceGroups.find(group => group.key === attendanceKey) || attendanceGroups[0] || null;
  const openAttendance = () => {
    setAttendanceKey(attendanceGroups[0]?.key || '');
    setAttendanceOpen(true);
  };
  const waitForAttendanceRender = () => new Promise(resolve => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
  });
  const captureAttendanceCanvas = () => {
    const element = document.getElementById('exam-attendance-register');
    if (!element) throw new Error('Unable to prepare the attendance register.');
    return html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      onclone: clonedDocument => {
        clonedDocument.querySelectorAll('#exam-attendance-register tfoot, #exam-attendance-register .attendance-controls').forEach(elementToHide => { elementToHide.style.display = 'none'; });
      },
    });
  };
  const appendAttendanceCanvasToPdf = (pdf, canvas, addPageBefore = false) => {
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 8;
    const footerHeight = 25;
    const imageWidth = pageWidth - (margin * 2);
    const contentHeight = pageHeight - margin - footerHeight;
    const pixelsPerMillimetre = canvas.width / imageWidth;
    const sliceHeight = Math.max(1, Math.floor(contentHeight * pixelsPerMillimetre));
    const institutionFooter = institution?.admission_letter_footer || institution?.motto || institution?.name || 'Official examination attendance record';
    const drawFooter = () => {
      const signoffY = pageHeight - footerHeight + 5;
      pdf.setDrawColor(120, 120, 120);
      pdf.setLineWidth(0.2);
      pdf.line(margin, signoffY - 3, pageWidth - margin, signoffY - 3);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8.5);
      const dottedGuide = '..............................';
      const invigilator = attendanceDetails.chiefInvigilator ? `${attendanceDetails.chiefInvigilator} ${dottedGuide}` : dottedGuide;
      pdf.text(`Chief invigilator: ${invigilator}`, margin, signoffY + 3);
      pdf.text(`Signature: ${dottedGuide}`, pageWidth / 2 - 25, signoffY + 3);
      pdf.text(`Date: ${dottedGuide}`, pageWidth - margin - 45, signoffY + 3);
      if (institutionFooter) {
        pdf.setFontSize(8);
        pdf.text(institutionFooter, pageWidth / 2, pageHeight - 5, { align: 'center' });
      }
    };
    for (let sourceY = 0, page = 0; sourceY < canvas.height; sourceY += sliceHeight, page += 1) {
      if (addPageBefore || page > 0) pdf.addPage();
      const currentSliceHeight = Math.min(sliceHeight, canvas.height - sourceY);
      const slice = document.createElement('canvas');
      slice.width = canvas.width;
      slice.height = currentSliceHeight;
      slice.getContext('2d').drawImage(canvas, 0, sourceY, canvas.width, currentSliceHeight, 0, 0, canvas.width, currentSliceHeight);
      pdf.addImage(slice.toDataURL('image/jpeg', 0.92), 'JPEG', margin, margin, imageWidth, currentSliceHeight / pixelsPerMillimetre);
      drawFooter();
    }
  };
  const downloadAttendancePdf = async () => {
    if (!activeAttendanceGroup) return;
    if (activeAttendanceGroup.attendanceMissing) {
      setNotice({ type: 'error', text: `Enter class attendance for all ${activeAttendanceGroup.attendanceMissing} paid candidate${activeAttendanceGroup.attendanceMissing === 1 ? '' : 's'} before downloading this register.` });
      return;
    }
    if (!activeAttendanceGroup.students.length) {
      setNotice({ type: 'error', text: 'No paid candidate has attendance of 70% or above for this unit.' });
      return;
    }
    setAttendanceDownloading(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      appendAttendanceCanvasToPdf(pdf, await captureAttendanceCanvas());
      const safeName = `${activeAttendanceGroup.unitCode || 'unit'}`.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();
      pdf.save(`exam-attendance-${safeName}-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      setNotice({ type: 'error', text: error?.message || 'Unable to download the attendance register.' });
    } finally {
      setAttendanceDownloading(false);
    }
  };
  const downloadAllAttendancePdf = async () => {
    const groupsWithMissingAttendance = attendanceGroups.filter(group => group.attendanceMissing);
    const downloadableGroups = attendanceGroups.filter(group => group.students.length);
    if (groupsWithMissingAttendance.length) {
      setNotice({ type: 'error', text: `Enter class attendance for all paid candidates before downloading all registers. ${groupsWithMissingAttendance.length} unit${groupsWithMissingAttendance.length === 1 ? '' : 's'} still have missing attendance.` });
      return;
    }
    if (!downloadableGroups.length) {
      setNotice({ type: 'error', text: 'No paid candidate has attendance of 70% or above for the selected bookings.' });
      return;
    }
    setAttendanceBulkDownloading(true);
    setAttendanceOpen(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      setAttendanceKey(downloadableGroups[0].key);
      await waitForAttendanceRender();
      for (let index = 0; index < downloadableGroups.length; index += 1) {
        if (index > 0) {
          setAttendanceKey(downloadableGroups[index].key);
          await waitForAttendanceRender();
        }
        appendAttendanceCanvasToPdf(pdf, await captureAttendanceCanvas(), index > 0);
      }
      pdf.save(`exam-attendance-all-units-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      setNotice({ type: 'error', text: error?.message || 'Unable to download all attendance registers.' });
    } finally {
      setAttendanceBulkDownloading(false);
      setAttendanceOpen(false);
    }
  };

  const tabBar = (canEnterMarks || canReviewApprovals || canViewHistorical) && (
    <div className="flex flex-wrap items-center gap-1 rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
      {canViewBookings && <button type="button" onClick={() => setActiveTab('bookings')} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${activeTab === 'bookings' ? 'bg-slate-950 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>Exam bookings</button>}
      {canEnterMarks && <button type="button" onClick={() => setActiveTab('marks')} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${activeTab === 'marks' ? 'bg-blue-700 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>Summative marks</button>}
      {canReviewApprovals && <button type="button" onClick={() => setActiveTab('approvals')} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${activeTab === 'approvals' ? 'bg-violet-700 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>Result approvals</button>}
      {canViewHistorical && <button type="button" onClick={() => setActiveTab('historical')} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${activeTab === 'historical' ? 'bg-blue-700 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>Historical marks</button>}
    </div>
  );

  // Department booking oversight is restricted to academic leadership. Trainers
  // entering from Teaching see only their paid-candidate marks workspace.
  if (!canViewBookings) {
    return canReviewApprovals
      ? <SummativeResultApprovals />
      : canEnterMarks
      ? <SummativeMarksEntry />
      : <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">Summative examination access is restricted to authorised staff.</div>;
  }

  if (activeTab === 'marks' && canEnterMarks) {
    return <div className="space-y-4">{tabBar}<SummativeMarksEntry /></div>;
  }

  if (activeTab === 'approvals' && canReviewApprovals) {
    return <div className="space-y-4">{tabBar}<SummativeResultApprovals /></div>;
  }

  if (activeTab === 'historical' && canViewHistorical) {
    return <div className="space-y-3">{tabBar}<StaffAcademicProgress /></div>;
  }

  return (
    <div className="space-y-4">
      {tabBar}
      <section className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-52">
          <div className="flex items-center gap-2"><h1 className="text-base font-bold text-slate-950">Summative exam bookings</h1><span className="text-xs font-mono text-slate-500">· {students.length} students</span></div>
          <p className="mt-0.5 text-xs text-slate-500">{payload.scope?.department_name || 'Academic oversight'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={downloadAllAttendancePdf} disabled={!attendanceGroups.length || loading || attendanceBulkDownloading} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"><Download className={`h-4 w-4 ${attendanceBulkDownloading ? 'animate-pulse' : ''}`} /> {attendanceBulkDownloading ? 'Preparing all attendance…' : 'Download all attendance'}</button>
          <button type="button" onClick={load} disabled={loading} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
        </div>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-12">
        <div className="relative sm:col-span-2 lg:col-span-2"><Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Student, admission or unit…" className="h-9 w-full rounded-lg border border-slate-200 pl-8 pr-3 text-xs focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100" /></div>
        <select value={period} onChange={event => setPeriod(event.target.value)} className="h-9 w-full lg:col-span-2 rounded-lg border border-slate-200 bg-white px-2 text-xs font-medium"><option value="all">All periods</option>{periods.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
        <select value={programId} onChange={event => { setProgramId(event.target.value); setModuleKey('all'); setUnitId('all'); }} className="h-9 w-full lg:col-span-2 rounded-lg border border-slate-200 bg-white px-2 text-xs font-medium"><option value="all">All programmes</option>{programs.map(program => <option key={program.id} value={program.id}>{program.name}</option>)}</select>
        <select value={moduleKey} onChange={event => { setModuleKey(event.target.value); setUnitId('all'); }} className="h-9 w-full lg:col-span-2 rounded-lg border border-slate-200 bg-white px-2 text-xs font-medium"><option value="all">All modules</option>{modules.map(module => <option key={module.id} value={module.id}>{module.name}</option>)}</select>
        <select value={unitId} onChange={event => setUnitId(event.target.value)} className="h-9 w-full lg:col-span-2 rounded-lg border border-slate-200 bg-white px-2 text-xs font-medium"><option value="all">All units of competency</option>{units.map(unit => <option key={unit.id} value={unit.id}>{unit.code ? `${unit.code} · ` : ''}{unit.name}</option>)}</select>
        <button type="button" onClick={openAttendance} disabled={!attendanceGroups.length || loading} className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-lg bg-slate-950 px-3 text-xs font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 lg:col-span-2"><Printer className="h-4 w-4" /> Generate attendance</button>
        </div>
      </section>

      {notice && <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{typeof notice === 'string' ? notice : notice.text}</div>}

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          ['Students booked', students.length, Users, 'bg-blue-50 text-blue-700'],
          ['Unit bookings', filtered.length, BookOpenCheck, 'bg-violet-50 text-violet-700'],
          ['Ledger invoices', `${allBilled}/${filtered.length}`, CheckCircle2, 'bg-emerald-50 text-emerald-700'],
          ['Exam fees billed', money(billedTotal), ReceiptText, 'bg-amber-50 text-amber-700'],
        ].map(([label, value, Icon, tone]) => <div key={label} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"><span className={`grid h-10 w-10 place-items-center rounded-lg ${tone}`}><Icon className="h-5 w-5" /></span><div><p className="text-xs text-slate-500">{label}</p><p className="text-lg font-bold text-slate-950">{value}</p></div></div>)}
      </section>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3"><Filter className="h-4 w-4 text-slate-500" /><h2 className="text-sm font-bold text-slate-950">Booked students by programme, module, class and unit of competency</h2></div>
        {loading ? (
          <div className="grid min-h-52 place-items-center text-sm text-slate-500"><span className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Loading bookings…</span></div>
        ) : students.length === 0 ? (
          <div className="p-12 text-center"><Users className="mx-auto h-9 w-9 text-slate-300" /><h3 className="mt-2 font-semibold text-slate-900">No student bookings found</h3><p className="mt-1 text-sm text-slate-500">Bookings will appear here immediately after students book their summative examinations.</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[940px] text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">Student</th><th className="px-4 py-3">Programme / class</th><th className="px-4 py-3">Booked units</th><th className="px-4 py-3">Types</th><th className="px-4 py-3 text-right">Billed</th><th className="px-4 py-3">Period</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {students.map(student => (
                  <tr key={student.key} className="align-top hover:bg-slate-50/60">
                    <td className="px-4 py-3"><p className="font-semibold text-slate-950">{student.studentName}</p><p className="mt-0.5 font-mono text-xs text-slate-500">{student.admissionNumber || 'No admission number'}</p></td>
                    <td className="px-4 py-3"><p className="font-medium text-slate-800">{student.programName}</p><p className="mt-0.5 text-xs text-slate-500">{student.className}</p></td>
                    <td className="px-4 py-3"><div className="flex flex-wrap gap-1.5">{student.units.map(unit => <span key={unit.booking_id} title={unit.unit_name} className="rounded border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-xs font-semibold text-slate-700">{unit.unit_code}</span>)}</div></td>
                    <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{[...new Set(student.units.map(unit => unit.unit_type))].map(type => <span key={type} className="rounded-full bg-blue-50 px-2 py-1 text-xs font-semibold capitalize text-blue-700">{type}</span>)}</div></td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{money(student.total)}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{student.periodName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {attendanceOpen && activeAttendanceGroup && (
        <div className="fixed inset-0 z-[80] overflow-y-auto bg-slate-950/50 p-4 print:static print:bg-white print:p-0">
          <div id="exam-attendance-register" className="mx-auto max-w-6xl rounded-xl bg-white shadow-2xl print:max-w-none print:rounded-none print:shadow-none">
            <div className="attendance-controls flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4 print:hidden">
              <div>
                <h2 className="text-lg font-bold text-slate-950">Exam attendance register</h2>
                <p className="mt-0.5 text-xs text-slate-500">Only paid candidates with recorded class attendance of 70% or above appear. {activeAttendanceGroup.attendanceMissing ? `${activeAttendanceGroup.attendanceMissing} attendance record${activeAttendanceGroup.attendanceMissing === 1 ? '' : 's'} still missing.` : `${activeAttendanceGroup.attendanceBelowThreshold} candidate${activeAttendanceGroup.attendanceBelowThreshold === 1 ? '' : 's'} below 70% omitted.`}</p>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={downloadAttendancePdf} disabled={attendanceDownloading || attendanceBulkDownloading || activeAttendanceGroup.attendanceMissing > 0 || !activeAttendanceGroup.students.length} title={activeAttendanceGroup.attendanceMissing ? 'Enter class attendance for every paid candidate first' : 'Only candidates with attendance of 70% or above are included'} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-700 px-3 text-xs font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"><Download className={`h-4 w-4 ${attendanceDownloading ? 'animate-pulse' : ''}`} /> {attendanceDownloading ? 'Preparing PDF…' : 'Download PDF'}</button>
                <button type="button" onClick={() => setAttendanceOpen(false)} aria-label="Close attendance register" className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"><X className="h-4 w-4" /></button>
              </div>
            </div>

            <div className="space-y-4 p-5 print:p-8">
              <div className="attendance-controls flex flex-wrap items-end justify-between gap-3 print:hidden">
                <label className="min-w-72 flex-1 text-xs font-semibold text-slate-700">Programme / module / unit
                  <Select
                    aria-label="Programme / module / unit"
                    className="mt-1 w-full"
                    value={activeAttendanceGroup.key}
                    onChange={setAttendanceKey}
                    searchable
                    placeholder="Select programme / module / unit"
                    options={attendanceGroups.map(group => ({
                      value: group.key,
                      label: `${group.programName} · Level ${group.programLevel || '—'} · Module ${group.moduleNumber || '—'} · ${group.unitCode}`,
                    }))}
                  />
                </label>
              </div>

              <header className="border-b-2 border-slate-900 pb-3 text-center text-black">
                <div className="mb-2 flex items-start justify-between text-[11px] font-semibold uppercase tracking-wide">
                  <span>Revision 3</span>
                  <span>{institution?.examination_attendance_reference || institution?.official_reference_prefix || 'EXAMS-ATT-001'}</span>
                </div>
                {institution?.logo_url ? <img src={institution.logo_url} alt={`${institution?.name || 'Institution'} logo`} className="mx-auto h-20 w-20 object-contain" /> : <div className="mx-auto grid h-20 w-20 place-items-center rounded-full border border-slate-400 text-[11px] font-bold">LOGO</div>}
                <h2 className="mt-2 text-2xl font-bold uppercase">{institution?.name || 'Institution name'}</h2>
                {institution?.motto && <p className="mt-0.5 text-sm italic">{institution.motto}</p>}
                <p className="mt-1 text-xs leading-4">{institution?.postal_address || institution?.address || ''}{institution?.phone ? ` · ${institution.phone}` : ''}{institution?.email ? ` · ${institution.email}` : ''}</p>
                <p className="mt-3 text-base font-bold uppercase underline">Examination attendance record form</p>
                <p className="mt-1 text-sm font-semibold uppercase">{activeAttendanceGroup.periodName} academic year examinations</p>
              </header>

              <div className="border-b border-slate-900 pb-3 text-center text-black">
                <p className="text-base font-bold uppercase">{activeAttendanceGroup.unitCode} — {activeAttendanceGroup.unitName}</p>
                <p className="mt-1 text-sm">Programme: {activeAttendanceGroup.programName} · Level {activeAttendanceGroup.programLevel || '—'} · Module {activeAttendanceGroup.moduleNumber || '—'}</p>
                <div className="mt-3 grid gap-4 text-left text-sm sm:grid-cols-3">
                  <div className="flex items-baseline gap-2"><strong className="shrink-0">Date:</strong><span className="min-w-0 flex-1 border-b border-dotted border-slate-500 px-1">{attendanceDetails.examDate || '\u00a0'}</span></div>
                  <div className="flex items-baseline gap-2"><strong className="shrink-0">Time:</strong><span className="min-w-0 flex-1 border-b border-dotted border-slate-500 px-1">{attendanceDetails.examTime || '\u00a0'}</span></div>
                  <div className="flex items-baseline gap-2"><strong className="shrink-0">Venue:</strong><span className="min-w-0 flex-1 border-b border-dotted border-slate-500 px-1">{attendanceDetails.venue || '\u00a0'}</span></div>
                </div>
              </div>

              <p className="text-[11px] leading-4 text-black"><strong>Instructions:</strong> Please indicate the type of examination and complete all candidate details before the attendance record is filed. Each candidate must sign against their name.</p>
              <div className="grid grid-cols-5 items-center gap-2 border-y border-slate-400 py-2 text-[11px] font-semibold uppercase text-black">
                <span className="text-left">Type of examination:</span><span className="text-center">1. Ordinary - O</span><span className="text-center">2. Supplementary - S</span><span className="text-center">3. Resit - R</span><span className="text-center">4. Special - SP</span>
              </div>

              <div className="attendance-controls grid gap-3 text-sm sm:grid-cols-4 print:hidden">
                {[
                  ['Exam date', 'examDate', 'date'],
                  ['Time', 'examTime', 'text'],
                  ['Venue', 'venue', 'text'],
                  ['Type of exam', 'examType', 'text'],
                ].map(([label, key, type]) => (
                  <label key={key} className="text-xs font-semibold text-slate-700">{label}
                    <input type={type} value={attendanceDetails[key] || ''} onChange={event => setAttendanceDetails(current => ({ ...current, [key]: event.target.value }))} className="mt-1 h-9 w-full rounded border border-slate-200 px-2 text-sm text-slate-900 print:border-0 print:px-0" />
                  </label>
                ))}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[850px] border-collapse text-base">
                  <thead>
                    <tr className="bg-slate-100 text-left text-sm uppercase tracking-wide text-slate-700">
                      <th className="border border-slate-400 px-2 py-2 text-center">S/No.</th>
                      <th className="border border-slate-400 px-2 py-2">Reg. No.</th>
                      <th className="border border-slate-400 px-2 py-2">Name</th>
                      <th className="border border-slate-400 px-2 py-2">Booklet serial no.</th>
                      <th className="border border-slate-400 px-2 py-2">Exam card no.</th>
                      <th className="border border-slate-400 px-2 py-2">Type of exam</th>
                      <th className="border border-slate-400 px-2 py-2">Signature</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeAttendanceGroup.students.map((row, index) => (
                      <tr key={row.booking_id || `${row.student_id}-${index}`} className="h-10">
                        <td className="border border-slate-400 px-2 text-center">{index + 1}</td>
                        <td className="border border-slate-400 px-2 font-mono text-sm">{row.admission_number || '—'}</td>
                        <td className="border border-slate-400 px-2 font-medium">{row.student_name || '—'}</td>
                        <td className="border border-slate-400 px-2"></td>
                        <td className="border border-slate-400 px-2"></td>
                        <td className="border border-slate-400 px-2"></td>
                        <td className="border border-slate-400 px-2"></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="print:table-footer-group">
                    <tr>
                      <td colSpan="7" className="px-0 pt-5">
                        <div className="attendance-signoff attendance-closing-section flex items-baseline gap-6 border-t border-slate-400 pt-4 text-sm text-black">
                          <div className="flex min-w-0 flex-1 items-baseline gap-2 whitespace-nowrap"><span className="shrink-0 font-semibold">Chief invigilator:</span><input value={attendanceDetails.chiefInvigilator} onChange={event => setAttendanceDetails(current => ({ ...current, chiefInvigilator: event.target.value }))} className="min-w-0 flex-1 border-0 border-b border-dotted border-slate-500 px-1 py-0.5 outline-none print:border-b print:border-dotted print:border-slate-500" /></div>
                          <div className="flex min-w-0 flex-1 items-baseline gap-2 whitespace-nowrap"><span className="shrink-0 font-semibold">Signature:</span><span className="min-w-0 flex-1 border-b border-dotted border-slate-500" /></div>
                          <div className="flex min-w-0 flex-1 items-baseline gap-2 whitespace-nowrap"><span className="shrink-0 font-semibold">Date:</span><span className="min-w-0 flex-1 border-b border-dotted border-slate-500" /></div>
                        </div>
                        <div className="institution-document-footer mt-3 border-t border-slate-300 pt-2 text-center text-[10px] text-slate-600">
                          {institution?.admission_letter_footer || institution?.motto || institution?.name || 'Official examination attendance record'}
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
          <style>{`@media print { body > * { display: none !important; } #exam-attendance-register { display: block !important; } #exam-attendance-register table tfoot { display: table-footer-group; } #exam-attendance-register .attendance-signoff { page-break-inside: avoid; } }`}</style>
        </div>
      )}
    </div>
  );
};

export default DepartmentSummativeExamDashboard;
